import React, { useRef } from "react"
import { useLang } from "hooks/useLang"

interface ScrollCarouselProps {
    perView: number
    spacing: number
    children: React.ReactNode
    scrollRef?: React.RefObject<HTMLDivElement | null>
    className?: string
}

const ScrollCarousel = ({
    perView,
    spacing,
    children,
    scrollRef,
    className = "",
}: ScrollCarouselProps) => {
    const { isAr } = useLang()
    const internalRef = useRef<HTMLDivElement>(null)
    const containerRef = scrollRef ?? internalRef

    const isDragging = useRef(false)
    const startX = useRef(0)
    const startScroll = useRef(0)

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current) return
        isDragging.current = true
        startX.current = event.pageX
        startScroll.current = containerRef.current.scrollLeft
    }

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging.current || !containerRef.current) return
        event.preventDefault()
        const deltaX = event.pageX - startX.current
        const direction = isAr ? -1 : 1
        containerRef.current.scrollLeft = startScroll.current - deltaX * direction
    }

    const handleMouseUp = () => {
        isDragging.current = false
    }

    const gapsInView = Math.ceil(perView) - 1
    const itemWidth = `calc((100% - ${spacing * gapsInView}px) / ${perView})`

    return (
        <div className="w-full">
            <div
                ref={containerRef}
                className={`w-full overflow-x-auto cursor-grab select-none scrollbar-hide ${className}`}
                style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    overscrollBehaviorX: "none",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onDragStart={(event) => event.preventDefault()}
            >
                <div
                    className="flex"
                    style={{
                        gap: `${spacing}px`,
                        transform: "translateZ(0)", // Force GPU layer for smoother scrolling on iOS
                    }}
                >
                    {React.Children.map(children, (child, index) => (
                        <div
                            key={index}
                            className="flex-shrink-0 overflow-hidden"
                            style={{
                                width: itemWidth,
                                minWidth: itemWidth,
                                maxWidth: itemWidth,
                            }}
                        >
                            {child}
                        </div>
                    ))}
                </div>
            </div>
            <style jsx global>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </div>
    )
}

export default ScrollCarousel
